import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Header from '../components/Header';
import {
  AttendanceStatus, downloadMyTrainingCalendar, fetchMyTrainingSchedule, formatTrainingDate, formatTrainingWindow,
} from '../api/trainingService';

const attendanceBadge: Record<AttendanceStatus, { label: string; className: string }> = {
  present: { label: 'Attended', className: 'bg-green-100 text-green-800' },
  late: { label: 'Attended (late)', className: 'bg-lime-100 text-lime-800' },
  excused: { label: 'Excused', className: 'bg-amber-100 text-amber-800' },
  absent: { label: 'Missed', className: 'bg-red-100 text-red-700' },
};

const Training: React.FC = () => {
  const schedule = useQuery({ queryKey: ['my-training'], queryFn: fetchMyTrainingSchedule });
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const data = schedule.data;
  const now = new Date();
  const nextId = data?.sessions.find((s) => s.status !== 'cancelled' && new Date(s.endsAt) > now)?.id;

  const addToCalendar = async () => {
    setCalendarError(null);
    try {
      await downloadMyTrainingCalendar();
    } catch {
      setCalendarError('Unable to download the calendar file. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-6">
        {schedule.isLoading && <p className="text-gray-600">Loading your training schedule…</p>}
        {schedule.isError && <p role="alert" className="text-red-700">Unable to load your training schedule.</p>}
        {schedule.isSuccess && !data && (
          <div className="bg-white shadow-lg rounded-xl p-6">
            <h1 className="text-xl font-semibold text-gray-900">Training</h1>
            <p className="text-gray-600 mt-2">You aren't enrolled in a training semester yet. Your hub leader will add you when your cohort starts.</p>
          </div>
        )}
        {data && (
          <>
            <div className="bg-white shadow-lg rounded-xl p-5 sm:p-6 space-y-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">{data.semester.name}</h1>
                <p className="text-sm text-gray-600">242 Network church planter training</p>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="rounded-lg bg-gray-50 px-4 py-2">
                  <p className="text-gray-500">Attended</p>
                  <p className="text-lg font-semibold text-gray-900">{data.summary.attended} <span className="text-sm font-normal text-gray-500">of {data.summary.required} required</span></p>
                </div>
                {data.enrollment.status === 'completed' && (
                  <div className="rounded-lg bg-green-50 px-4 py-2 text-green-800">
                    <p className="font-semibold">Training complete</p>
                    {data.enrollment.completedAt && <p className="text-xs">{new Date(data.enrollment.completedAt).toLocaleDateString()}</p>}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={addToCalendar} className="inline-flex items-center px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700">Add all sessions to my calendar</button>
                {data.semester.planterManualUrl && (
                  <a href={data.semester.planterManualUrl} target="_blank" rel="noreferrer" className="inline-flex items-center px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Planter manual</a>
                )}
              </div>
              {calendarError && <p role="alert" className="text-sm text-red-700">{calendarError}</p>}
            </div>

            <ol className="space-y-4">
              {data.sessions.map((s) => {
                const cancelled = s.status === 'cancelled';
                const past = new Date(s.endsAt) <= now;
                const trainers = s.trainers.filter((t) => t.trainer).map((t) => t.trainer!.name);
                return (
                  <li key={s.id} className={`bg-white shadow rounded-xl p-5 ${s.id === nextId ? 'ring-2 ring-primary-500' : ''} ${cancelled || past ? 'opacity-75' : ''}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        {s.id === nextId && <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Next session</p>}
                        <h2 className={`text-lg font-semibold text-gray-900 ${cancelled ? 'line-through' : ''}`}>{s.displayTitle}</h2>
                        <p className="text-sm text-gray-700">{formatTrainingDate(s.startsAt, s.timezone)} · {formatTrainingWindow(s.startsAt, s.endsAt, s.timezone)}</p>
                      </div>
                      {cancelled && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Cancelled</span>}
                      {!cancelled && s.attendance && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${attendanceBadge[s.attendance].className}`}>{attendanceBadge[s.attendance].label}</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 mt-2">
                      {s.venue ? (
                        <>
                          <span className="font-medium">{s.venue.name}</span>
                          {s.venue.address && <> · {s.venue.address}</>}
                          {s.venue.mapUrl && <> · <a className="text-primary-700 underline" href={s.venue.mapUrl} target="_blank" rel="noreferrer">Map</a></>}
                        </>
                      ) : 'Location to be announced'}
                    </p>
                    {s.venue?.notes && <p className="text-xs text-gray-500">{s.venue.notes}</p>}
                    {trainers.length > 0 && <p className="text-sm text-gray-600 mt-1">Trainers: {trainers.join(' & ')}</p>}
                    {s.outcomes.length > 0 && (
                      <ul className="mt-3 text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                        {s.outcomes.map((o) => <li key={o.id}>{o.text}</li>)}
                      </ul>
                    )}
                    {s.notes && <p className="text-sm italic text-gray-500 mt-2">{s.notes}</p>}
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </main>
    </div>
  );
};

export default Training;
